import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../lib/apiFetch";
import {
  Plus, Trash2, Send, CheckCircle, Clock, XCircle,
  ChevronDown, ChevronRight, Smartphone, Image, Video, FileText,
  Type, X, Check, Copy, Variable, Upload, Sparkles, Loader2, Link,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

interface WaTemplate {
  name: string;
  category: string;
  language: string;
  header_type?: string;
  header?: string;
  header_media_url?: string;
  body: string;
  footer?: string;
  buttons: { type: string; text: string; value?: string }[];
  variables: { key: string; description: string; sample: string }[];
  status: string;
  msg91_id?: string;
  created_at?: number;
}

interface TemplateForm {
  name: string;
  category: string;
  language: string;
  header_type: "none" | "text" | "image" | "video" | "document";
  header: string;
  header_media_url: string;
  body: string;
  footer: string;
  buttons: { type: "QUICK_REPLY" | "URL" | "PHONE"; text: string; value: string }[];
  variables: { key: string; description: string; sample: string }[];
}

const EMPTY_FORM: TemplateForm = {
  name: "", category: "MARKETING", language: "en",
  header_type: "none", header: "", header_media_url: "",
  body: "", footer: "", buttons: [], variables: [],
};

const CATEGORIES = ["MARKETING", "UTILITY", "AUTHENTICATION"];
const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "en_US", label: "English (US)" },
  { code: "hi", label: "Hindi" },
];

const PURPOSE_PRESETS = [
  "Welcome a new lead and invite them to start a free trial",
  "Re-engage a cold lead who hasn't responded",
  "Remind a trial user to upgrade before their trial expires",
  "Notify a lead about a missed class they can reschedule",
  "Share class schedule and invite them to book a slot",
  "Promote a limited-time offer or discount",
  "Request feedback after a class",
  "Announce a new feature or instructor",
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function extractVarKeys(text: string): string[] {
  const matches = text.match(/\{\{(\w+)\}\}/g) || [];
  return [...new Set(matches.map(m => m.replace(/\{\{|\}\}/g, "")))];
}

function applyVars(text: string, vars: { key: string; sample: string }[]): string {
  let out = text;
  for (const v of vars) out = out.replace(new RegExp(`\\{\\{${v.key}\\}\\}`, "g"), v.sample || `[${v.key}]`);
  return out;
}

function StatusBadge({ status }: { status: string }) {
  if (status === "APPROVED") return (
    <span className="flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
      <CheckCircle className="w-3 h-3" /> Approved
    </span>
  );
  if (status === "REJECTED") return (
    <span className="flex items-center gap-1 text-xs font-medium text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
      <XCircle className="w-3 h-3" /> Rejected
    </span>
  );
  return (
    <span className="flex items-center gap-1 text-xs font-medium text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded-full">
      <Clock className="w-3 h-3" /> Pending
    </span>
  );
}

// ─── WhatsApp Phone Preview ───────────────────────────────────────────────────

function PhonePreview({ form }: { form: TemplateForm }) {
  const previewBody = applyVars(form.body, form.variables);
  const previewHeader = applyVars(form.header, form.variables);

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative w-56 rounded-[2rem] border-4 border-gray-800 bg-gray-800 shadow-2xl overflow-hidden">
        <div className="bg-gray-800 px-4 py-1 flex justify-between items-center">
          <span className="text-white text-[10px]">9:41</span>
          <span className="text-white text-[10px]">●●●</span>
        </div>
        <div className="bg-[#075e54] px-3 py-2 flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-green-300 flex items-center justify-center text-[10px] font-bold text-green-900 flex-shrink-0">Y</div>
          <div className="min-w-0">
            <p className="text-white text-xs font-medium truncate">Yogain Wellness</p>
            <p className="text-green-200 text-[10px]">Business Account</p>
          </div>
        </div>
        <div className="bg-[#e5ddd5] min-h-48 p-2 flex flex-col gap-1">
          <div className="bg-white rounded-lg rounded-tl-none shadow-sm overflow-hidden max-w-[90%] self-start">
            {form.header_type === "image" && (
              <div className="bg-gray-200 h-24 flex items-center justify-center overflow-hidden">
                {form.header_media_url ? (
                  <img src={form.header_media_url} alt="" className="w-full h-full object-cover" onError={e => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
                ) : (
                  <div className="flex flex-col items-center text-gray-400">
                    <Image className="w-6 h-6" />
                    <span className="text-[10px] mt-1">Image</span>
                  </div>
                )}
              </div>
            )}
            {form.header_type === "video" && (
              <div className="bg-gray-800 h-24 flex items-center justify-center">
                <div className="flex flex-col items-center text-gray-300">
                  <Video className="w-6 h-6" />
                  <span className="text-[10px] mt-1">Video</span>
                </div>
              </div>
            )}
            {form.header_type === "document" && (
              <div className="bg-blue-50 px-3 py-2 flex items-center gap-2 border-b border-blue-100">
                <FileText className="w-5 h-5 text-blue-500 flex-shrink-0" />
                <span className="text-xs text-blue-600 truncate">{form.header_media_url ? form.header_media_url.split("/").pop() : "document.pdf"}</span>
              </div>
            )}
            {form.header_type === "text" && form.header && (
              <div className="px-2.5 pt-2 pb-1">
                <p className="text-xs font-semibold text-gray-900">{previewHeader}</p>
              </div>
            )}
            <div className="px-2.5 py-2">
              <p className="text-[11px] text-gray-800 whitespace-pre-wrap leading-relaxed">
                {previewBody || <span className="text-gray-400 italic">Message body…</span>}
              </p>
            </div>
            {form.footer && (
              <div className="px-2.5 pb-1.5">
                <p className="text-[10px] text-gray-400">{form.footer}</p>
              </div>
            )}
            <div className="px-2.5 pb-1 flex justify-end">
              <span className="text-[10px] text-gray-400">12:00 ✓✓</span>
            </div>
            {form.buttons.length > 0 && (
              <div className="border-t border-gray-100">
                {form.buttons.map((btn, i) => (
                  <div key={i} className="text-center text-[11px] text-[#075e54] font-medium py-1.5 border-b border-gray-100 last:border-b-0">
                    {btn.text || "Button"}
                  </div>
                ))}
              </div>
            )}
          </div>
          <p className="text-[9px] text-gray-500 self-start ml-1">Business message · Tap to learn more</p>
        </div>
      </div>

      {form.variables.length > 0 && (
        <div className="w-56 bg-white rounded-xl border border-gray-200 p-3">
          <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
            <Variable className="w-3 h-3" /> Preview values
          </p>
          <div className="space-y-1.5">
            {form.variables.map(v => (
              <div key={v.key} className="flex items-center gap-1.5 text-xs">
                <code className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded font-mono text-[10px] flex-shrink-0">{`{{${v.key}}}`}</code>
                <span className="text-gray-600 truncate">{v.sample || `[${v.key}]`}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── AI Generate Modal ────────────────────────────────────────────────────────

function AIGenerateModal({ onClose, onGenerated }: {
  onClose: () => void;
  onGenerated: (form: Partial<TemplateForm>) => void;
}) {
  const [purpose, setPurpose] = useState("");
  const [category, setCategory] = useState("MARKETING");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  async function generate() {
    if (!purpose.trim()) return;
    setGenerating(true);
    setError("");
    try {
      const r = await apiFetch("/api/templates/ai-generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ purpose, category }),
      });
      const data = await r.json();
      if (!data.success) { setError(data.error || "Generation failed"); return; }
      onGenerated({ ...data.template, header_type: data.template.header_type || "none" });
    } catch (e) {
      setError(String(e));
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Generate Template with AI</h3>
              <p className="text-xs text-gray-400 mt-0.5">Powered by Gemini · uses your knowledge base</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Purpose presets */}
          <div>
            <label className="text-xs font-medium text-gray-700 block mb-2">Quick pick a purpose</label>
            <div className="flex flex-wrap gap-1.5">
              {PURPOSE_PRESETS.map(p => (
                <button key={p} onClick={() => setPurpose(p)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${purpose === p ? "border-purple-400 bg-purple-50 text-purple-700" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>
                  {p.split(" ").slice(0, 5).join(" ")}…
                </button>
              ))}
            </div>
          </div>

          {/* Custom purpose */}
          <div>
            <label className="text-xs font-medium text-gray-700 block mb-1.5">Or describe the template purpose</label>
            <textarea value={purpose} onChange={e => setPurpose(e.target.value)} rows={3}
              placeholder="e.g. Send a warm welcome to someone who just signed up for a free trial, highlight our AI posture tracking, and encourage them to attend their first class"
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none" />
          </div>

          {/* Category */}
          <div>
            <label className="text-xs font-medium text-gray-700 block mb-1.5">Template category</label>
            <div className="flex gap-2">
              {CATEGORIES.map(c => (
                <button key={c} onClick={() => setCategory(c)}
                  className={`flex-1 py-2 text-xs font-medium rounded-lg border transition-colors ${category === c ? "border-purple-400 bg-purple-50 text-purple-700" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">{error}</div>
          )}

          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 text-sm py-2.5 rounded-xl hover:bg-gray-50 transition-colors">Cancel</button>
            <button onClick={generate} disabled={!purpose.trim() || generating}
              className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm py-2.5 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors">
              {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4" /> Generate</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Create/Edit Overlay ──────────────────────────────────────────────────────

function CreateOverlay({ onClose, onSaved, initialData }: {
  onClose: () => void;
  onSaved: () => void;
  initialData?: Partial<TemplateForm>;
}) {
  const [form, setForm] = useState<TemplateForm>({ ...EMPTY_FORM, ...initialData });
  const [saveResult, setSaveResult] = useState<{ submitted: boolean; error?: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [mediaInputMode, setMediaInputMode] = useState<"url" | "upload">("url");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync initialData when it changes (AI generation flow)
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setForm({ ...EMPTY_FORM, ...initialData });
    }
  }, [initialData]);

  // Auto-detect variables from body + header and sync with form.variables
  useEffect(() => {
    const detected = extractVarKeys([form.header, form.body, form.footer].join(" "));
    setForm(f => {
      const existing = new Map(f.variables.map(v => [v.key, v]));
      const merged = detected.map(k => existing.get(k) || {
        key: k,
        description: "",
        sample: k === "name" ? "Priya" : k === "org" ? "Yogain" : k === "trial" ? "7" : "",
      });
      const manual = f.variables.filter(v => !detected.includes(v.key));
      return { ...f, variables: [...merged, ...manual] };
    });
  }, [form.header, form.body, form.footer]);

  async function handleFileUpload(file: File) {
    setUploading(true);
    setUploadError("");
    try {
      const ext = file.name.split(".").pop()?.toLowerCase() || "";
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const r = await apiFetch("/api/templates/upload-media", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: base64, ext }),
      });
      const data = await r.json();
      if (data.url) {
        setField("header_media_url", data.url);
      } else {
        setUploadError(data.error || "Upload failed");
      }
    } catch (e) {
      setUploadError(String(e));
    } finally {
      setUploading(false);
    }
  }

  const saveMutation = useMutation({
    mutationFn: (t: TemplateForm) => apiFetch("/api/templates", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...t, header: form.header_type === "text" ? t.header : undefined }),
    }).then(r => r.json()),
    onSuccess: (data) => { setSaveResult(data); if (data.success) onSaved(); },
  });

  const setField = <K extends keyof TemplateForm>(k: K, v: TemplateForm[K]) =>
    setForm(f => ({ ...f, [k]: v }));

  const HEADER_TYPES = [
    { id: "none", label: "None", icon: X },
    { id: "text", label: "Text", icon: Type },
    { id: "image", label: "Image", icon: Image },
    { id: "video", label: "Video", icon: Video },
    { id: "document", label: "Document", icon: FileText },
  ];

  const acceptForType = form.header_type === "image" ? "image/jpeg,image/png,image/webp"
    : form.header_type === "video" ? "video/mp4"
    : form.header_type === "document" ? "application/pdf" : "";

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      {/* Top bar */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Create WhatsApp Template</h2>
          <p className="text-xs text-gray-500 mt-0.5">Templates must be approved by Meta before use (1–3 business days)</p>
        </div>
        <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Two-column body */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── LEFT: Form ── */}
        <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6 bg-gray-50">

          {/* Name + Category + Language */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <h3 className="text-sm font-semibold text-gray-700">Basic Info</h3>
            <div>
              <label className="text-xs font-medium text-gray-600">Template Name <span className="text-red-500">*</span></label>
              <input value={form.name}
                onChange={e => setField("name", e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))}
                placeholder="yogain_new_lead_welcome"
                className="mt-1.5 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-green-500" />
              <p className="text-xs text-gray-400 mt-1">Lowercase, numbers and underscores only</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-600">Category</label>
                <select value={form.category} onChange={e => setField("category", e.target.value)}
                  className="mt-1.5 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 bg-white">
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Language</label>
                <select value={form.language} onChange={e => setField("language", e.target.value)}
                  className="mt-1.5 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 bg-white">
                  {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Header */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <h3 className="text-sm font-semibold text-gray-700">Header <span className="text-xs font-normal text-gray-400">(optional)</span></h3>
            <div className="flex gap-2">
              {HEADER_TYPES.map(ht => {
                const Icon = ht.icon;
                const active = form.header_type === ht.id;
                return (
                  <button key={ht.id} onClick={() => { setField("header_type", ht.id as any); setUploadError(""); }}
                    className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${active ? "border-green-500 bg-green-50 text-green-700" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}>
                    <Icon className="w-4 h-4" />
                    {ht.label}
                  </button>
                );
              })}
            </div>

            {form.header_type === "text" && (
              <input value={form.header}
                onChange={e => setField("header", e.target.value)}
                placeholder="Header text (e.g. Welcome to Yogain! 🧘‍♀️)"
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            )}

            {(form.header_type === "image" || form.header_type === "video" || form.header_type === "document") && (
              <div className="space-y-3">
                {/* URL / Upload toggle */}
                <div className="flex rounded-lg border border-gray-200 overflow-hidden w-fit">
                  <button onClick={() => setMediaInputMode("url")}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${mediaInputMode === "url" ? "bg-gray-100 text-gray-800" : "text-gray-500 hover:bg-gray-50"}`}>
                    <Link className="w-3 h-3" /> URL
                  </button>
                  <button onClick={() => setMediaInputMode("upload")}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors border-l border-gray-200 ${mediaInputMode === "upload" ? "bg-gray-100 text-gray-800" : "text-gray-500 hover:bg-gray-50"}`}>
                    <Upload className="w-3 h-3" /> Upload
                  </button>
                </div>

                {mediaInputMode === "url" ? (
                  <div>
                    <input value={form.header_media_url}
                      onChange={e => setField("header_media_url", e.target.value)}
                      placeholder={`${form.header_type === "image" ? "Image" : form.header_type === "video" ? "Video" : "Document"} URL (https://…)`}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                    <p className="text-xs text-gray-400 mt-1">Must be a publicly accessible URL. Meta downloads and caches it.</p>
                  </div>
                ) : (
                  <div>
                    <input ref={fileInputRef} type="file" accept={acceptForType} className="hidden"
                      onChange={e => { const f = e.target.files?.[0]; if (f) handleFileUpload(f); e.target.value = ""; }} />
                    {form.header_media_url ? (
                      <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        {form.header_type === "image" ? (
                          <img src={form.header_media_url} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-12 h-12 bg-green-100 rounded flex items-center justify-center flex-shrink-0">
                            {form.header_type === "video" ? <Video className="w-5 h-5 text-green-600" /> : <FileText className="w-5 h-5 text-green-600" />}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-green-700">Uploaded</p>
                          <p className="text-xs text-green-600 truncate">{form.header_media_url.split("/").pop()}</p>
                        </div>
                        <button onClick={() => setField("header_media_url", "")}
                          className="text-green-500 hover:text-red-500 flex-shrink-0"><X className="w-4 h-4" /></button>
                      </div>
                    ) : (
                      <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                        className="w-full border-2 border-dashed border-gray-300 rounded-xl p-6 flex flex-col items-center gap-2 text-gray-400 hover:border-green-400 hover:text-green-600 transition-colors disabled:opacity-50">
                        {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" />}
                        <span className="text-sm font-medium">{uploading ? "Uploading…" : `Click to upload ${form.header_type}`}</span>
                        <span className="text-xs">
                          {form.header_type === "image" ? "JPEG, PNG — max 5MB" : form.header_type === "video" ? "MP4 — max 10MB" : "PDF — max 10MB"}
                        </span>
                      </button>
                    )}
                    {uploadError && <p className="text-xs text-red-500 mt-1">{uploadError}</p>}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Body */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700">Body <span className="text-red-500">*</span></h3>
              <span className="text-xs text-gray-400">{form.body.length} / 1024</span>
            </div>
            <textarea value={form.body}
              onChange={e => setField("body", e.target.value)}
              rows={6} maxLength={1024}
              placeholder={"Hi {{name}}! 👋 Welcome to {{org}}!\n\nUse {{variable_name}} for dynamic values.\nExample: your trial is {{trial}} days free."}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-green-500 resize-none" />
            <p className="text-xs text-gray-400">Use <code className="bg-gray-100 px-1 rounded font-mono">{"{{variable_name}}"}</code> for personalisation — variables are auto-detected below</p>
          </div>

          {/* Footer */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
            <h3 className="text-sm font-semibold text-gray-700">Footer <span className="text-xs font-normal text-gray-400">(optional)</span></h3>
            <input value={form.footer}
              onChange={e => setField("footer", e.target.value)}
              placeholder="Reply STOP to opt out"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
          </div>

          {/* Variables */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-700">Variables</h3>
                <p className="text-xs text-gray-400 mt-0.5">Auto-detected from body. Add descriptions and sample values.</p>
              </div>
              <button onClick={() => setForm(f => ({
                ...f, variables: [...f.variables, { key: "", description: "", sample: "" }]
              }))} className="text-xs text-green-600 hover:text-green-700 font-medium flex items-center gap-1">
                <Plus className="w-3 h-3" /> Add manually
              </button>
            </div>
            {form.variables.length === 0 ? (
              <p className="text-xs text-gray-400 italic">No variables detected yet — type {"{{variable_name}}"} in the body above</p>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-2 text-xs font-medium text-gray-500 pb-1 border-b border-gray-100">
                  <span>Variable</span>
                  <span>Description</span>
                  <span>Sample value</span>
                </div>
                {form.variables.map((v, i) => (
                  <div key={i} className="grid grid-cols-3 gap-2 items-center">
                    <div className="flex items-center gap-1">
                      <code className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs font-mono truncate flex-1">
                        {v.key ? `{{${v.key}}}` : ""}
                      </code>
                      {!extractVarKeys([form.header, form.body, form.footer].join(" ")).includes(v.key) && (
                        <input value={v.key}
                          onChange={e => setForm(f => ({ ...f, variables: f.variables.map((x, j) => j === i ? { ...x, key: e.target.value } : x) }))}
                          placeholder="key"
                          className="border border-gray-300 rounded px-2 py-1 text-xs font-mono focus:outline-none w-full" />
                      )}
                    </div>
                    <input value={v.description}
                      onChange={e => setForm(f => ({ ...f, variables: f.variables.map((x, j) => j === i ? { ...x, description: e.target.value } : x) }))}
                      placeholder="e.g. Lead's first name"
                      className="border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none" />
                    <div className="flex items-center gap-1">
                      <input value={v.sample}
                        onChange={e => setForm(f => ({ ...f, variables: f.variables.map((x, j) => j === i ? { ...x, sample: e.target.value } : x) }))}
                        placeholder={v.key === "name" ? "Priya" : v.key === "org" ? "Yogain" : "sample…"}
                        className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none" />
                      <button onClick={() => setForm(f => ({ ...f, variables: f.variables.filter((_, j) => j !== i) }))}
                        className="text-gray-300 hover:text-red-400 flex-shrink-0"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-700">Buttons <span className="text-xs font-normal text-gray-400">(max 3, optional)</span></h3>
              </div>
              {form.buttons.length < 3 && (
                <button onClick={() => setForm(f => ({ ...f, buttons: [...f.buttons, { type: "QUICK_REPLY", text: "", value: "" }] }))}
                  className="text-xs text-green-600 hover:text-green-700 font-medium flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Add Button
                </button>
              )}
            </div>
            {form.buttons.length === 0 ? (
              <p className="text-xs text-gray-400 italic">No buttons — click Add Button to add quick replies or CTAs</p>
            ) : (
              <div className="space-y-2">
                {form.buttons.map((btn, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <select value={btn.type}
                      onChange={e => setForm(f => ({ ...f, buttons: f.buttons.map((b, j) => j === i ? { ...b, type: e.target.value as any } : b) }))}
                      className="border border-gray-300 rounded-lg px-2.5 py-2 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-green-500">
                      <option value="QUICK_REPLY">Quick Reply</option>
                      <option value="URL">Visit URL</option>
                      <option value="PHONE">Call Phone</option>
                    </select>
                    <input value={btn.text}
                      onChange={e => setForm(f => ({ ...f, buttons: f.buttons.map((b, j) => j === i ? { ...b, text: e.target.value } : b) }))}
                      placeholder="Button label"
                      className="flex-1 border border-gray-300 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500" />
                    {(btn.type === "URL" || btn.type === "PHONE") && (
                      <input value={btn.value}
                        onChange={e => setForm(f => ({ ...f, buttons: f.buttons.map((b, j) => j === i ? { ...b, value: e.target.value } : b) }))}
                        placeholder={btn.type === "URL" ? "https://…" : "+91XXXXXXXXXX"}
                        className="flex-1 border border-gray-300 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500" />
                    )}
                    <button onClick={() => setForm(f => ({ ...f, buttons: f.buttons.filter((_, j) => j !== i) }))}
                      className="p-1.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
            <button onClick={() => saveMutation.mutate(form)}
              disabled={!form.name || !form.body || saveMutation.isPending}
              className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 rounded-lg font-medium text-sm transition-colors">
              {saveMutation.isPending ? "Submitting to MSG91…" : "Submit for Meta Approval →"}
            </button>
            {saveResult && (
              <div className={`text-sm rounded-lg p-3 ${saveResult.submitted ? "bg-green-50 text-green-700 border border-green-200" : "bg-yellow-50 text-yellow-700 border border-yellow-200"}`}>
                {saveResult.submitted
                  ? "✓ Submitted to MSG91 — Meta approval takes 1–3 business days."
                  : `Saved locally. MSG91 error: ${saveResult.error || "check credentials"}`}
              </div>
            )}
          </div>

          <div className="h-8" />
        </div>

        {/* ── RIGHT: Live Preview ── */}
        <div className="w-96 flex-shrink-0 border-l border-gray-200 bg-white overflow-y-auto flex flex-col items-center py-8 px-6 gap-6">
          <div className="text-center">
            <p className="text-sm font-semibold text-gray-700 flex items-center gap-2 justify-center">
              <Smartphone className="w-4 h-4" /> Live Preview
            </p>
            <p className="text-xs text-gray-400 mt-0.5">Updates as you type</p>
          </div>
          <PhonePreview form={form} />
          {form.name && (
            <div className="w-56 bg-gray-50 rounded-lg border border-gray-200 px-3 py-2 flex items-center justify-between">
              <span className="text-xs font-mono text-gray-600 truncate">{form.name}</span>
              <button onClick={() => { navigator.clipboard.writeText(form.name); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                className="ml-2 flex-shrink-0 text-gray-400 hover:text-gray-600">
                {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
          )}
          <div className="w-56 bg-blue-50 rounded-lg border border-blue-100 p-3 text-xs text-blue-700 space-y-1">
            <p className="font-medium">Template tips</p>
            <p>• Marketing templates need clear opt-out option</p>
            <p>• Avoid promotional language like "FREE" or "WIN"</p>
            <p>• Variables must match at send time</p>
            <p>• Images must be JPEG/PNG, max 5MB</p>
          </div>
        </div>

      </div>
    </div>
  );
}

// ─── Template Card ────────────────────────────────────────────────────────────

function TemplateCard({ template, onDelete, onTest }: {
  template: WaTemplate;
  onDelete: (name: string) => void;
  onTest: (t: WaTemplate) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 select-none"
        onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-3 min-w-0">
          {expanded ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
          <div className="min-w-0">
            <p className="font-medium text-gray-900 text-sm truncate font-mono">{template.name}</p>
            <p className="text-xs text-gray-500 truncate">{template.body?.slice(0, 70)}{(template.body?.length ?? 0) > 70 ? "…" : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">{template.category}</span>
          <StatusBadge status={template.status} />
        </div>
      </div>
      {expanded && (
        <div className="border-t border-gray-100 p-4 flex gap-6">
          <div className="flex-1 space-y-3 min-w-0">
            {template.header && (
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Header</p>
                <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-2">{template.header}</p>
              </div>
            )}
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Body</p>
              <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-2 whitespace-pre-wrap">{template.body}</p>
            </div>
            {template.footer && (
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Footer</p>
                <p className="text-sm text-gray-400 bg-gray-50 rounded-lg p-2">{template.footer}</p>
              </div>
            )}
            {(template.variables || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {(template.variables || []).map(v => (
                  <span key={v.key} className="text-xs bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded-full font-mono">{`{{${v.key}}}`}</span>
                ))}
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <button onClick={() => onTest(template)}
                className="flex items-center gap-1.5 text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg font-medium transition-colors">
                <Send className="w-3 h-3" /> Test Send
              </button>
              <button onClick={() => onDelete(template.name)}
                className="flex items-center gap-1.5 text-xs border border-red-200 text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg font-medium transition-colors">
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            </div>
          </div>
          <div className="flex-shrink-0 hidden lg:block">
            <PhonePreview form={{
              ...EMPTY_FORM,
              header_type: (template.header_type as any) || (template.header ? "text" : "none"),
              header: template.header || "",
              header_media_url: template.header_media_url || "",
              body: template.body || "",
              footer: template.footer || "",
              buttons: (template.buttons || []).map(b => ({ ...b, type: b.type as any, value: b.value || "" })),
              variables: (template.variables || []),
            }} />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Test Modal ───────────────────────────────────────────────────────────────

function TestModal({ template, onClose }: { template: WaTemplate; onClose: () => void }) {
  const [phone, setPhone] = useState("");
  const [params, setParams] = useState<string[]>((template.variables || []).map(v => v.sample || ""));
  const [result, setResult] = useState<{ success: boolean; error?: string } | null>(null);
  const [sending, setSending] = useState(false);

  const vars = template.variables || [];
  const previewBody = applyVars(template.body, vars.map((v, i) => ({ key: v.key, sample: params[i] || v.sample || "" })));

  async function send() {
    setSending(true); setResult(null);
    try {
      const r = await apiFetch("/api/templates/test", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, templateName: template.name, params }),
      });
      setResult(await r.json());
    } finally { setSending(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Test Template</h3>
            <p className="text-xs font-mono text-gray-400 mt-0.5">{template.name}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs font-medium text-gray-700">Send to Phone Number</label>
            <input value={phone} onChange={e => setPhone(e.target.value)}
              placeholder="919XXXXXXXXX  (include country code)"
              className="mt-1.5 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
          </div>
          {vars.length > 0 && (
            <div>
              <label className="text-xs font-medium text-gray-700">Variable Values</label>
              <div className="mt-2 space-y-2">
                {vars.map((v, i) => (
                  <div key={v.key} className="flex items-center gap-2">
                    <code className="text-xs text-blue-700 bg-blue-50 px-2 py-1.5 rounded font-mono w-28 truncate flex-shrink-0">{`{{${v.key}}}`}</code>
                    <input value={params[i] || ""}
                      onChange={e => setParams(p => { const n = [...p]; n[i] = e.target.value; return n; })}
                      placeholder={v.description || `Value for ${v.key}`}
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500" />
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 mb-2">Message preview</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{previewBody}</p>
          </div>
          {result && (
            <div className={`rounded-xl p-3 text-sm ${result.success ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
              {result.success ? "✓ Sent successfully via MSG91!" : `✗ ${result.error}`}
            </div>
          )}
          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 text-sm py-2.5 rounded-xl hover:bg-gray-50">Cancel</button>
            <button onClick={send} disabled={!phone || sending}
              className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm py-2.5 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors">
              <Send className="w-4 h-4" />
              {sending ? "Sending…" : "Send Test"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Templates() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiInitialData, setAiInitialData] = useState<Partial<TemplateForm> | undefined>(undefined);
  const [testTemplate, setTestTemplate] = useState<WaTemplate | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["templates"],
    queryFn: () => apiFetch("/api/templates").then(r => r.json()),
    refetchInterval: 30000,
  });

  const deleteMutation = useMutation({
    mutationFn: (name: string) => apiFetch(`/api/templates/${encodeURIComponent(name)}`, { method: "DELETE" }).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["templates"] }),
  });

  const templates: WaTemplate[] = data?.templates || [];
  const configured: boolean = data?.msg91Configured ?? false;
  const approved = templates.filter(t => t.status === "APPROVED").length;
  const pending  = templates.filter(t => t.status === "PENDING").length;

  function handleAiGenerated(form: Partial<TemplateForm>) {
    setAiInitialData(form);
    setShowAiModal(false);
    setShowCreate(true);
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Page header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">WhatsApp Templates</h1>
          <p className="text-sm text-gray-500 mt-0.5">Create and submit WhatsApp templates for Meta approval via MSG91</p>
        </div>
        <div className="flex items-center gap-3">
          {templates.length > 0 && (
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span className="flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5 text-green-500" />{approved} approved</span>
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-yellow-500" />{pending} pending</span>
            </div>
          )}
          <div className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium border ${configured ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${configured ? "bg-green-500" : "bg-red-400"}`} />
            MSG91 {configured ? "Connected" : "Not configured"}
          </div>
          <button onClick={() => setShowAiModal(true)}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white text-sm px-4 py-2 rounded-lg font-medium transition-colors">
            <Sparkles className="w-4 h-4" /> Generate with AI
          </button>
          <button onClick={() => { setAiInitialData(undefined); setShowCreate(true); }}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm px-4 py-2 rounded-lg font-medium transition-colors">
            <Plus className="w-4 h-4" /> New Template
          </button>
        </div>
      </div>

      {/* Template list */}
      <div className="flex-1 overflow-y-auto p-6">
        {isLoading ? (
          <div className="text-center py-16 text-gray-400">
            <div className="animate-spin w-6 h-6 border-2 border-gray-300 border-t-green-500 rounded-full mx-auto mb-3" />
            Loading templates…
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Smartphone className="w-7 h-7 text-gray-400" />
            </div>
            <p className="text-gray-700 font-medium">No templates yet</p>
            <p className="text-sm text-gray-400 mt-1 max-w-sm mx-auto">Create your first template to start sending official WhatsApp messages without ban risk</p>
            <div className="flex gap-3 justify-center mt-5">
              <button onClick={() => setShowAiModal(true)} className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white text-sm px-5 py-2.5 rounded-xl font-medium transition-colors">
                <Sparkles className="w-4 h-4" /> Generate with AI
              </button>
              <button onClick={() => { setAiInitialData(undefined); setShowCreate(true); }} className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm px-5 py-2.5 rounded-xl font-medium transition-colors">
                <Plus className="w-4 h-4" /> Create manually
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 max-w-4xl">
            {templates.map(t => (
              <TemplateCard key={t.name} template={t}
                onDelete={n => { if (window.confirm(`Delete template "${n}"?`)) deleteMutation.mutate(n); }}
                onTest={setTestTemplate}
              />
            ))}
          </div>
        )}
      </div>

      {/* Overlays */}
      {showAiModal && (
        <AIGenerateModal
          onClose={() => setShowAiModal(false)}
          onGenerated={handleAiGenerated}
        />
      )}
      {showCreate && (
        <CreateOverlay
          onClose={() => setShowCreate(false)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ["templates"] }); setShowCreate(false); }}
          initialData={aiInitialData}
        />
      )}
      {testTemplate && <TestModal template={testTemplate} onClose={() => setTestTemplate(null)} />}
    </div>
  );
}
